<script>
  import { selectedSymbol, currentQuote, selectedSymbolDetails, ohlcvData, formatCurrency, formatCrypto, formatPercentage } from '../../lib/store.js';
  
  // Track previous values for animation
  let previousBid = null;
  let previousAsk = null;
  let previousLastPrice = null;
  let bidUpdated = false;
  let askUpdated = false;
  let priceUpdated = false;
  
  // Use the most recent price data available
  $: lastPrice = (() => {
    // Priority order: currentQuote (most recent), then selectedSymbolDetails
    if ($currentQuote?.lastTradePrice !== undefined && $currentQuote?.lastTradePrice !== null) {
      return $currentQuote.lastTradePrice;
    }
    return $selectedSymbolDetails?.lastPrice || 0;
  })();
  
  $: priceChange = (() => {
    // Use currentQuote if available as it's more recent
    if ($currentQuote?.priceChangePercentage !== undefined && $currentQuote?.priceChangePercentage !== null) {
      return $currentQuote.priceChangePercentage;
    }
    return $selectedSymbolDetails?.priceChange || 0;
  })();
  
  $: isPositive = priceChange >= 0;
  
  // Calculate 24h high/low from OHLCV data (last 24 hours of data)
  $: dayHigh = (() => {
    if ($currentQuote?.highPrice24Hrs !== undefined && $currentQuote?.highPrice24Hrs !== null) {
      return $currentQuote.highPrice24Hrs;
    }
    if ($ohlcvData && $ohlcvData.length > 0) {
      return $ohlcvData[$ohlcvData.length - 1]?.high;
    }
    return $selectedSymbolDetails?.highPrice;
  })();
  
  $: dayLow = (() => {
    if ($currentQuote?.lowPrice24Hrs !== undefined && $currentQuote?.lowPrice24Hrs !== null) {
      return $currentQuote.lowPrice24Hrs;
    }
    if ($ohlcvData && $ohlcvData.length > 0) {
      return $ohlcvData[$ohlcvData.length - 1]?.low;
    }
    return $selectedSymbolDetails?.lowPrice;
  })();
  
  $: volume24h = $currentQuote?.baseVol24Hrs || $selectedSymbolDetails?.baseVol;
  
  $: bid = $currentQuote?.bid || $selectedSymbolDetails?.bid;
  $: ask = $currentQuote?.ask || $selectedSymbolDetails?.ask;
  $: spread = $currentQuote?.spreadPercentage;
  
  // Track last price changes for animation
  $: {
    if (previousLastPrice !== null && previousLastPrice !== lastPrice && Math.abs(previousLastPrice - lastPrice) > 0.01) {
      priceUpdated = true;
      setTimeout(() => {
        priceUpdated = false;
      }, 1000);
    }
    previousLastPrice = lastPrice;
  }
  
  // Track bid changes for animation
  $: {
    if (previousBid !== null && previousBid !== bid && Math.abs(previousBid - bid) > 0.01) {
      bidUpdated = true;
      setTimeout(() => {
        bidUpdated = false;
      }, 1000);
    }
    previousBid = bid;
  }
  
  // Track ask changes for animation
  $: {
    if (previousAsk !== null && previousAsk !== ask && Math.abs(previousAsk - ask) > 0.01) {
      askUpdated = true;
      setTimeout(() => {
        askUpdated = false;
      }, 1000);
    }
    previousAsk = ask;
  }
</script>

<div class="header-quote">
  <div class="symbol-section">
    <div class="symbol-name">
      <h2>{$selectedSymbol}</h2>
      <span class="asset-name">{$selectedSymbolDetails?.baseAssetName}</span>
    </div>
  </div>
  
  <div class="divider"></div>
  
  <div class="stats-section">
    <!-- Current Price without change -->
    <div class="stat-item">
      <span class="stat-label">Last Price</span>
      <span class="stat-value" class:price-flash={priceUpdated}>
        {formatCurrency(lastPrice, $selectedSymbolDetails?.quoteDecimalPlaces || 2)}
      </span>
    </div>
    
    <!-- Bid and Ask moved after price -->
    <div class="stat-item">
      <span class="stat-label">Bid</span>
      <span class="stat-value bid" class:price-flash={bidUpdated}>
        {formatCurrency(bid, $selectedSymbolDetails?.quoteDecimalPlaces || 2)}
      </span>
    </div>
    
    <div class="stat-item">
      <span class="stat-label">Ask</span>
      <span class="stat-value ask" class:price-flash={askUpdated}>
        {formatCurrency(ask, $selectedSymbolDetails?.quoteDecimalPlaces || 2)}
      </span>
    </div>
    
    <!-- 24h stats -->
    <div class="stat-item">
      <span class="stat-label">24h High</span>
      <span class="stat-value">
        {formatCurrency(dayHigh, $selectedSymbolDetails?.quoteDecimalPlaces || 2)}
      </span>
    </div>
    
    <div class="stat-item">
      <span class="stat-label">24h Low</span>
      <span class="stat-value">
        {formatCurrency(dayLow, $selectedSymbolDetails?.quoteDecimalPlaces || 2)}
      </span>
    </div>
    
    <div class="stat-item">
      <span class="stat-label">24h Volume</span>
      <span class="stat-value">
        {formatCrypto(volume24h, 2)}
      </span>
    </div>
    
    <div class="stat-item">
      <span class="stat-label">Spread</span>
      <span class="stat-value">
        {formatPercentage(spread)}
      </span>
    </div>
  </div>
</div>

<style>
  @import './HeaderQuote.css';
</style>